import React, { useEffect, useState, memo } from 'react';
import { HeadphonesIcon, ScaleIcon, FileTextIcon, EditIcon, MailIcon, PlusIcon, CheckIcon, SearchIcon, ServerIcon, GitBranchIcon, CpuIcon, MemoryStickIcon, XIcon, DownloadIcon, UploadIcon, ExternalLinkIcon, ChevronRightIcon, InfoIcon, RefreshCwIcon, LayersIcon, DatabaseIcon, BotIcon, HardDriveIcon, SettingsIcon, ScaleIcon as ScaleDynamicIcon, ArrowUpCircleIcon, AlertCircleIcon, BookOpenIcon, LinkIcon, CheckCircleIcon, XCircleIcon, AlertTriangleIcon, PackageIcon, BadgeCheckIcon, MessageSquareIcon, GlobeIcon, KeyIcon, ShieldIcon, CloudIcon, SaveIcon, EyeIcon, EyeOffIcon, UserIcon, ToggleLeftIcon, ToggleRightIcon } from 'lucide-react';
import { Drawer } from '../components/Drawer';
export function AppMarketplace() {
  const [searchTerm, setSearchTerm] = useState('');
  const [addedApps, setAddedApps] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const [isOfflineDrawerOpen, setIsOfflineDrawerOpen] = useState(false);
  const [isMcpDrawerOpen, setIsMcpDrawerOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<any>(null);
  const [deploymentStep, setDeploymentStep] = useState(0);
  const [deploymentMethod, setDeploymentMethod] = useState('upload');
  const [deploymentUrl, setDeploymentUrl] = useState('');
  const [bootstrapOption, setBootstrapOption] = useState('vllm');
  const [offlineInstallMethod, setOfflineInstallMethod] = useState('upload');
  const [offlineInstallUrl, setOfflineInstallUrl] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('pending');
  // MCP Server state
  const [mcpServers, setMcpServers] = useState<any[]>([{
    id: 'mcp-1',
    name: 'Production MCP Server',
    url: 'https://mcp-prod.company.com',
    apiKey: '••••••••••••••••',
    status: 'active',
    models: 12,
    lastSync: '10 minutes ago',
    description: 'Primary production MCP server with high availability',
    region: 'US West'
  }, {
    id: 'mcp-2',
    name: 'Staging MCP Server',
    url: 'https://mcp-staging.company.com',
    apiKey: '••••••••••••••••',
    status: 'inactive',
    models: 8,
    lastSync: '3 hours ago',
    description: 'Staging environment for testing new models and configurations',
    region: 'US East'
  }, {
    id: 'mcp-3',
    name: 'Development MCP Server',
    url: 'https://mcp-dev.company.com',
    apiKey: '••••••••••••••••',
    status: 'active',
    models: 15,
    lastSync: '2 days ago',
    description: 'Development environment for internal testing',
    region: 'EU West'
  }]);
  const [newMcpServer, setNewMcpServer] = useState({
    name: '',
    url: '',
    apiKey: '',
    description: '',
    region: 'US West'
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [mcpTestStatus, setMcpTestStatus] = useState('');
  const [selectedMcpServer, setSelectedMcpServer] = useState<any>(null);
  // Mock system infrastructure data
  const systemInfrastructure = {
    gpu: {
      available: '40GB',
      type: 'NVIDIA A100'
    },
    memory: '64GB',
    storage: '500GB',
    cpuCores: 32
  };
  const categories = [{
    id: 'all',
    label: 'All',
    icon: <LayersIcon size={16} data-id="element-353" />
  }, {
    id: 'models',
    label: 'Models',
    icon: <DatabaseIcon size={16} data-id="element-354" />
  }, {
    id: 'applications',
    label: 'Applications',
    icon: <BotIcon size={16} data-id="element-355" />
  }, {
    id: 'workflows',
    label: 'Workflows',
    icon: <GitBranchIcon size={16} data-id="element-356" />
  }, {
    id: 'mcp',
    label: 'MCP Servers',
    icon: <ServerIcon size={16} data-id="element-357" />
  }];
  const modelApps = [{
    id: 'llama-4',
    name: 'Llama 4',
    version: '1.0.0',
    description: "Meta's open-weight, multimodal model handling text, images, and short video with Mixture-of-Experts architecture.",
    provider: 'Meta AI',
    providerLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/512px-Meta_Platforms_Inc._logo.svg.png',
    category: 'models',
    color: 'bg-blue-100 text-blue-600',
    iconBg: 'bg-blue-600',
    requirements: {
      gpu: '80GB+',
      memory: '128GB',
      storage: '140GB'
    },
    deployed: true,
    hasUpdate: false,
    compatibility: {
      status: 'insufficient',
      issues: ['GPU memory insufficient (need 80GB+)', 'System memory insufficient (need 128GB)']
    }
  }, {
    id: 'llama-4-8b',
    name: 'Llama 4 (8B)',
    version: '1.0.0',
    description: "Lightweight version of Meta's Llama 4 model with 8B parameters.",
    provider: 'Meta AI',
    providerLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/7b/Meta_Platforms_Inc._logo.svg/512px-Meta_Platforms_Inc._logo.svg.png',
    category: 'models',
    color: 'bg-blue-100 text-blue-600',
    iconBg: 'bg-blue-600',
    requirements: {
      gpu: '16GB+',
      memory: '32GB',
      storage: '16GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'deepseek-r1',
    name: 'DeepSeek R1',
    version: '1.0.0',
    description: 'Reasoning-focused 671B parameter model excelling in mathematics and coding.',
    provider: 'DeepSeek',
    providerLogo: 'https://avatars.githubusercontent.com/u/140644241?s=200&v=4',
    category: 'models',
    color: 'bg-indigo-100 text-indigo-600',
    iconBg: 'bg-indigo-600',
    requirements: {
      gpu: '80GB+',
      memory: '256GB',
      storage: '180GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'insufficient',
      issues: ['GPU memory insufficient (need 80GB+)', 'System memory insufficient (need 256GB)', 'Storage insufficient (need 180GB)']
    }
  }, {
    id: 'deepseek-v3',
    name: 'DeepSeek V3.1',
    version: '1.0.0',
    updateVersion: '1.1.0',
    description: 'Mixture-of-Experts architecture with 671B parameters and strong benchmark performance.',
    provider: 'DeepSeek',
    providerLogo: 'https://avatars.githubusercontent.com/u/140644241?s=200&v=4',
    category: 'models',
    color: 'bg-indigo-100 text-indigo-600',
    iconBg: 'bg-indigo-600',
    requirements: {
      gpu: '80GB+',
      memory: '256GB',
      storage: '180GB'
    },
    deployed: true,
    hasUpdate: true,
    compatibility: {
      status: 'insufficient',
      issues: ['GPU memory insufficient (need 80GB+)', 'System memory insufficient (need 256GB)', 'Storage insufficient (need 180GB)']
    }
  }, {
    id: 'qwen-3',
    name: 'Qwen 3',
    version: '1.0.0',
    description: "Alibaba's hybrid Mixture-of-Experts models with efficient compute requirements.",
    provider: 'Alibaba',
    providerLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b6/Alibaba_Group_logo.svg/1280px-Alibaba_Group_logo.svg.png',
    category: 'models',
    color: 'bg-orange-100 text-orange-600',
    iconBg: 'bg-orange-600',
    requirements: {
      gpu: '40GB+',
      memory: '64GB',
      storage: '80GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'gemma-3',
    name: 'Gemma 3',
    version: '1.0.0',
    updateVersion: '1.1.0',
    description: "Google's family of open-weight LLMs designed for efficiency and responsible AI.",
    provider: 'Google',
    providerLogo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/53/Google_%22G%22_Logo.svg/512px-Google_%22G%22_Logo.svg.png',
    category: 'models',
    color: 'bg-green-100 text-green-600',
    iconBg: 'bg-green-600',
    requirements: {
      gpu: '24GB+',
      memory: '48GB',
      storage: '60GB'
    },
    deployed: true,
    hasUpdate: true,
    compatibility: {
      status: 'warning',
      issues: ['System memory slightly below recommended (need 48GB)']
    }
  }, {
    id: 'mistral-small-3',
    name: 'Mistral Small 3',
    version: '1.0.0',
    description: '24B parameter model optimized for latency and efficiency.',
    provider: 'Mistral AI',
    providerLogo: 'https://avatars.githubusercontent.com/u/132333856?s=200&v=4',
    category: 'models',
    color: 'bg-purple-100 text-purple-600',
    iconBg: 'bg-purple-600',
    requirements: {
      gpu: '24GB+',
      memory: '32GB',
      storage: '48GB'
    },
    deployed: true,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'olmo-2',
    name: 'OLMo 2',
    version: '1.0.0',
    description: 'Open-source LLM in 7B and 13B parameter sizes promoting transparency for AI research.',
    provider: 'Allen Institute for AI',
    providerLogo: 'https://allenai.org/favicon.ico',
    category: 'models',
    color: 'bg-teal-100 text-teal-600',
    iconBg: 'bg-teal-600',
    requirements: {
      gpu: '16GB+',
      memory: '24GB',
      storage: '26GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'magistral-small',
    name: 'Magistral Small',
    version: '1.0.0',
    description: "Mistral AI's 24B parameter model focused on advanced reasoning tasks.",
    provider: 'Mistral AI',
    providerLogo: 'https://avatars.githubusercontent.com/u/132333856?s=200&v=4',
    category: 'models',
    color: 'bg-purple-100 text-purple-600',
    iconBg: 'bg-purple-600',
    requirements: {
      gpu: '24GB+',
      memory: '32GB',
      storage: '48GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'kimi-k2',
    name: 'Kimi K2',
    version: '1.0.0',
    description: "Moonshot AI's model featuring enhanced coding and agent capabilities.",
    provider: 'Moonshot AI',
    providerLogo: 'https://avatars.githubusercontent.com/u/131613761?s=200&v=4',
    category: 'models',
    color: 'bg-rose-100 text-rose-600',
    iconBg: 'bg-rose-600',
    requirements: {
      gpu: '40GB+',
      memory: '64GB',
      storage: '80GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }];
  const apps = [{
    id: 'customer-care',
    name: 'Customer Care Agent',
    version: '1.0.0',
    description: 'AI-powered customer support agent that handles inquiries and resolves issues 24/7.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'applications',
    color: 'bg-purple-100 text-purple-600',
    iconBg: 'bg-purple-600',
    requirements: {
      gpu: '16GB+',
      memory: '16GB',
      storage: '5GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'legal-agent',
    name: 'Legal Agent',
    version: '1.0.0',
    updateVersion: '1.1.0',
    description: 'Assists with legal document review, contract analysis, and legal research.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'applications',
    color: 'bg-indigo-100 text-indigo-600',
    iconBg: 'bg-indigo-600',
    requirements: {
      gpu: '24GB+',
      memory: '32GB',
      storage: '10GB'
    },
    deployed: true,
    hasUpdate: true,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'document-analyzer',
    name: 'Document Analyzer',
    version: '1.0.0',
    description: 'Extract insights, summarize content, and analyze documents automatically.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'applications',
    color: 'bg-green-100 text-green-600',
    iconBg: 'bg-green-600',
    requirements: {
      gpu: '16GB+',
      memory: '16GB',
      storage: '8GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'speech-writer',
    name: 'Speech Writer',
    version: '1.0.0',
    description: 'Creates compelling speeches, presentations, and talking points for any occasion.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'applications',
    color: 'bg-amber-100 text-amber-600',
    iconBg: 'bg-amber-600',
    requirements: {
      gpu: '16GB+',
      memory: '16GB',
      storage: '5GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }];
  const workflows = [{
    id: 'data-processing',
    name: 'Data Processing Pipeline',
    version: '1.0.0',
    description: 'End-to-end workflow for data ingestion, processing, and analysis.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'workflows',
    color: 'bg-cyan-100 text-cyan-600',
    iconBg: 'bg-cyan-600',
    requirements: {
      gpu: '8GB+',
      memory: '16GB',
      storage: '20GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }, {
    id: 'content-generation',
    name: 'Content Generation Engine',
    version: '1.0.0',
    description: 'Automated workflow for creating and publishing content across channels.',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'workflows',
    color: 'bg-rose-100 text-rose-600',
    iconBg: 'bg-rose-600',
    requirements: {
      gpu: '16GB+',
      memory: '32GB',
      storage: '10GB'
    },
    deployed: true,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }];
  // MCP Servers
  const mcpServerItems = [{
    id: 'mcp-server-1',
    name: 'MCP Server',
    version: '1.0.0',
    description: 'Model Control Plane server for managing model deployments and configurations',
    provider: 'Amoebix',
    providerLogo: 'https://placehold.co/100x100/4F46E5/FFFFFF?text=A&font=montserrat',
    category: 'mcp',
    color: 'bg-slate-100 text-slate-600',
    iconBg: 'bg-slate-600',
    requirements: {
      gpu: 'N/A',
      memory: '8GB',
      storage: '20GB'
    },
    deployed: false,
    hasUpdate: false,
    compatibility: {
      status: 'compatible',
      issues: []
    }
  }];
  // Combine all apps
  const allApps = [...modelApps, ...apps, ...workflows, ...mcpServerItems];
  // Filter apps based on search term and category
  const filteredApps = allApps.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) || app.description.toLowerCase().includes(searchTerm.toLowerCase()) || app.provider.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || app.category === activeCategory;
    return matchesSearch && matchesCategory;
  });
  const handleAddApp = (appId: string) => {
    if (addedApps.includes(appId)) {
      setAddedApps(addedApps.filter(id => id !== appId));
    } else {
      setAddedApps([...addedApps, appId]);
    }
  };
  const openAppDetails = (app: any) => {
    setSelectedApp(app);
    setIsDrawerOpen(true);
    setDeploymentStep(0);
  };
  const openHelpDrawer = () => {
    setIsHelpDrawerOpen(true);
  };
  const openOfflineInstallDrawer = () => {
    setIsOfflineDrawerOpen(true);
  };
  const openMcpDrawer = () => {
    setNewMcpServer({
      name: '',
      url: '',
      apiKey: '',
      description: '',
      region: 'US West'
    });
    setShowApiKey(false);
    setMcpTestStatus('');
    setIsMcpDrawerOpen(true);
  };
  const openMcpDetailsDrawer = (server: any) => {
    setSelectedMcpServer(server);
    setIsMcpDrawerOpen(true);
  };
  const handleAddMcpServer = () => {
    // Validate form
    if (!newMcpServer.name || !newMcpServer.url || !newMcpServer.apiKey) {
      return;
    }
    // Create new MCP server
    const newServer = {
      id: `mcp-${Date.now()}`,
      name: newMcpServer.name,
      url: newMcpServer.url,
      apiKey: '••••••••••••••••',
      rawApiKey: newMcpServer.apiKey,
      status: 'active',
      models: 0,
      lastSync: 'Never',
      description: newMcpServer.description,
      region: newMcpServer.region
    };
    setMcpServers([...mcpServers, newServer]);
    setIsMcpDrawerOpen(false);
  };
  const toggleMcpServerStatus = (serverId: string) => {
    setMcpServers(mcpServers.map(server => server.id === serverId ? {
      ...server,
      status: server.status === 'active' ? 'inactive' : 'active'
    } : server));
  };
  const testMcpConnection = () => {
    setMcpTestStatus('testing');
    // Simulate API call
    setTimeout(() => {
      if (newMcpServer.url && newMcpServer.apiKey) {
        setMcpTestStatus('success');
      } else {
        setMcpTestStatus('failed');
      }
    }, 1500);
  };
  const getRequirementColor = (level: string) => {
    if (level.includes('80GB+')) return 'text-red-500';
    if (level.includes('40GB+')) return 'text-orange-500';
    if (level.includes('24GB+')) return 'text-yellow-500';
    return 'text-green-500';
  };
  const getCompatibilityStatus = (status: string) => {
    switch (status) {
      case 'compatible':
        return {
          icon: <CheckCircleIcon size={16} className="text-green-500" data-id="element-358" />,
          text: 'Compatible',
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'warning':
        return {
          icon: <AlertTriangleIcon size={16} className="text-yellow-500" data-id="element-359" />,
          text: 'Compatible with warnings',
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100'
        };
      case 'insufficient':
        return {
          icon: <XCircleIcon size={16} className="text-red-500" data-id="element-360" />,
          text: 'Insufficient resources',
          color: 'text-red-600',
          bgColor: 'bg-red-100'
        };
      default:
        return {
          icon: <InfoIcon size={16} className="text-blue-500" data-id="element-361" />,
          text: 'Unknown',
          color: 'text-blue-600',
          bgColor: 'bg-blue-100'
        };
    }
  };
  // Stats for the summary section
  const stats = {
    total: allApps.length,
    deployed: allApps.filter(app => app.deployed).length,
    updates: allApps.filter(app => app.hasUpdate).length,
    models: {
      total: modelApps.length,
      deployed: modelApps.filter(app => app.deployed).length
    },
    applications: {
      total: apps.length,
      deployed: apps.filter(app => app.deployed).length
    },
    workflows: {
      total: workflows.length,
      deployed: workflows.filter(app => app.deployed).length
    },
    mcp: {
      total: mcpServers.length,
      active: mcpServers.filter(server => server.status === 'active').length
    }
  };
  // Get category icon for cards
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'models':
        return <DatabaseIcon size={16} data-id="element-362" />;
      case 'applications':
        return <BotIcon size={16} data-id="element-363" />;
      case 'workflows':
        return <GitBranchIcon size={16} data-id="element-364" />;
      case 'mcp':
        return <ServerIcon size={16} data-id="element-365" />;
      default:
        return <LayersIcon size={16} data-id="element-366" />;
    }
  };
  // Handle offline install verification
  const handleVerifyOfflinePackage = () => {
    setVerificationStatus('verifying');
    setTimeout(() => {
      if (offlineInstallUrl.includes('amoebix') || offlineInstallMethod === 'upload') {
        setVerificationStatus('verified');
      } else {
        setVerificationStatus('failed');
      }
    }, 1500);
  };
  // Reset deployment form when drawer closes
  useEffect(() => {
    if (!isDrawerOpen) {
      setTimeout(() => {
        setDeploymentStep(0);
        setDeploymentMethod('upload');
        setDeploymentUrl('');
        setBootstrapOption('vllm');
      }, 300);
    }
  }, [isDrawerOpen]);
  // Reset offline install form when drawer closes
  useEffect(() => {
    if (!isOfflineDrawerOpen) {
      setTimeout(() => {
        setOfflineInstallMethod('upload');
        setOfflineInstallUrl('');
        setVerificationStatus('pending');
      }, 300);
    }
  }, [isOfflineDrawerOpen]);
  return <div className="space-y-6 bg-black text-white" data-id="element-367">
      <div className="flex items-center justify-between" data-id="element-368">
        <h2 className="text-xl font-bold text-white" data-id="element-369">App Marketplace</h2>
        <div className="flex items-center space-x-2" data-id="element-370">
          {/* Summary stats in a compact format */}
          <div className="flex items-center space-x-2 bg-[#121212] rounded-lg px-3 py-1.5 border border-[#222222]" data-id="element-371">
            <div className="flex items-center" data-id="element-372">
              <span className="text-xs text-gray-400 mr-1" data-id="element-373">Installed:</span>
              <span className="text-sm font-medium text-gray-300" data-id="element-374">
                {stats.deployed}
              </span>
            </div>
            <div className="h-4 w-px bg-gray-700" data-id="element-375"></div>
            <div className="flex items-center" data-id="element-376">
              <span className="text-xs text-gray-400 mr-1" data-id="element-377">Updates:</span>
              <span className="text-sm font-medium text-yellow-400" data-id="element-378">
                {stats.updates}
              </span>
            </div>
          </div>
          <button onClick={openOfflineInstallDrawer} className="p-2 rounded-md bg-[#121212] text-gray-300 hover:bg-[#1a1a1a] transition-colors" title="Offline Installation" data-id="element-379">
            <PackageIcon size={18} data-id="element-380" />
          </button>
          <button onClick={openHelpDrawer} className="p-2 rounded-full bg-blue-900 text-blue-300 hover:bg-blue-800 transition-colors" data-id="element-381">
            <InfoIcon size={18} data-id="element-382" />
          </button>
        </div>
      </div>
      {/* Category Selection */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-383">
        <div className={`bg-gradient-to-br from-blue-900 to-indigo-900 rounded-lg p-5 text-white cursor-pointer transition-transform hover:scale-[1.02] shadow-md ${activeCategory === 'models' ? 'ring-4 ring-blue-700' : ''}`} onClick={() => setActiveCategory('models')} data-id="element-384">
          <div className="flex items-center justify-between mb-3" data-id="element-385">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg" data-id="element-386">
              <DatabaseIcon size={24} data-id="element-387" />
            </div>
            <span className="text-sm font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full" data-id="element-388">
              {stats.models.deployed}/{stats.models.total} Deployed
            </span>
          </div>
          <h3 className="text-xl font-bold mb-1" data-id="element-389">Models</h3>
          <p className="text-sm text-blue-200" data-id="element-390">
            Large language models from leading providers
          </p>
        </div>
        <div className={`bg-gradient-to-br from-purple-900 to-pink-900 rounded-lg p-5 text-white cursor-pointer transition-transform hover:scale-[1.02] shadow-md ${activeCategory === 'applications' ? 'ring-4 ring-purple-700' : ''}`} onClick={() => setActiveCategory('applications')} data-id="element-391">
          <div className="flex items-center justify-between mb-3" data-id="element-392">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg" data-id="element-393">
              <BotIcon size={24} data-id="element-394" />
            </div>
            <span className="text-sm font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full" data-id="element-395">
              {stats.applications.deployed}/{stats.applications.total} Deployed
            </span>
          </div>
          <h3 className="text-xl font-bold mb-1" data-id="element-396">Applications</h3>
          <p className="text-sm text-purple-200" data-id="element-397">
            Pre-built AI applications for specific use cases
          </p>
        </div>
        <div className={`bg-gradient-to-br from-emerald-900 to-teal-900 rounded-lg p-5 text-white cursor-pointer transition-transform hover:scale-[1.02] shadow-md ${activeCategory === 'workflows' ? 'ring-4 ring-emerald-700' : ''}`} onClick={() => setActiveCategory('workflows')} data-id="element-398">
          <div className="flex items-center justify-between mb-3" data-id="element-399">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg" data-id="element-400">
              <GitBranchIcon size={24} data-id="element-401" />
            </div>
            <span className="text-sm font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full" data-id="element-402">
              {stats.workflows.deployed}/{stats.workflows.total} Deployed
            </span>
          </div>
          <h3 className="text-xl font-bold mb-1" data-id="element-403">Workflows</h3>
          <p className="text-sm text-emerald-200" data-id="element-404">
            Automated AI workflow templates for complex processes
          </p>
        </div>
        <div className={`bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg p-5 text-white cursor-pointer transition-transform hover:scale-[1.02] shadow-md ${activeCategory === 'mcp' ? 'ring-4 ring-slate-700' : ''}`} onClick={() => setActiveCategory('mcp')} data-id="element-405">
          <div className="flex items-center justify-between mb-3" data-id="element-406">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg" data-id="element-407">
              <ServerIcon size={24} data-id="element-408" />
            </div>
            <span className="text-sm font-medium bg-white bg-opacity-20 px-2 py-1 rounded-full" data-id="element-409">
              {stats.mcp.active}/{stats.mcp.total} Active
            </span>
          </div>
          <h3 className="text-xl font-bold mb-1" data-id="element-410">MCP Servers</h3>
          <p className="text-sm text-slate-200" data-id="element-411">
            Model Control Plane servers for distributed deployments
          </p>
        </div>
      </div>
      {/* Search and filter */}
      <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4" data-id="element-412">
        <div className="relative flex-1" data-id="element-413">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-414">
            <SearchIcon size={18} className="text-gray-400" data-id="element-415" />
          </div>
          <input type="text" className="pl-10 pr-4 py-2 w-full bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-white" placeholder="Search marketplace..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-416" />
        </div>
        <div className="flex space-x-2 overflow-x-auto pb-1" data-id="element-417">
          {categories.map(category => <button key={category.id} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center whitespace-nowrap ${activeCategory === category.id ? 'bg-blue-600 text-white shadow-md' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setActiveCategory(category.id)} data-id="element-418">
              <span className="mr-1.5" data-id="element-419">{category.icon}</span>
              {category.label}
            </button>)}
        </div>
      </div>
      {/* MCP Servers section */}
      {activeCategory === 'mcp' && <div className="space-y-4" data-id="element-420">
          <div className="flex justify-between items-center" data-id="element-421">
            <h3 className="text-lg font-medium text-white" data-id="element-422">MCP Servers</h3>
            <button onClick={openMcpDrawer} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm" data-id="element-423">
              <PlusIcon size={16} className="mr-1.5" data-id="element-424" />
              Add MCP Server
            </button>
          </div>
          {mcpServers.length > 0 ? <div className="bg-[#0F0F0F] rounded-lg shadow-sm border border-[#222222] overflow-hidden" data-id="element-425">
              <div className="overflow-x-auto" data-id="element-426">
                <table className="min-w-full divide-y divide-[#333333]" data-id="element-427">
                  <thead className="bg-[#121212]" data-id="element-428">
                    <tr data-id="element-429">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-430">
                        Server
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-431">
                        URL
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-432">
                        Region
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-433">
                        Models
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-434">
                        Last Sync
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-435">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider" data-id="element-436">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#0F0F0F] divide-y divide-[#222222]" data-id="element-437">
                    {mcpServers.map(server => <tr key={server.id} className="hover:bg-[#1a1a1a] cursor-pointer" onClick={() => openMcpDetailsDrawer(server)} data-id="element-438">
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-439">
                          <div className="flex items-center" data-id="element-440">
                            <div className="flex-shrink-0 h-10 w-10 bg-slate-600 rounded-full flex items-center justify-center text-white" data-id="element-441">
                              <ServerIcon size={18} data-id="element-442" />
                            </div>
                            <div className="ml-4" data-id="element-443">
                              <div className="text-sm font-medium text-white" data-id="element-444">
                                {server.name}
                              </div>
                              <div className="text-xs text-gray-400 line-clamp-1" data-id="element-445">
                                {server.description}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400" data-id="element-446">
                          {server.url}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400" data-id="element-447">
                          {server.region}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300" data-id="element-448">
                          {server.models}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400" data-id="element-449">
                          {server.lastSync}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-450">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${server.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-300'}`} data-id="element-451">
                            {server.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-452">
                          <button onClick={e => {
                    e.stopPropagation();
                    toggleMcpServerStatus(server.id);
                  }} className={`mr-2 inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${server.status === 'active' ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-green-900 text-green-300 hover:bg-green-800'}`} data-id="element-453">
                            {server.status === 'active' ? <>
                                <ToggleRightIcon size={14} className="mr-1" data-id="element-454" />
                                Disable
                              </> : <>
                                <ToggleLeftIcon size={14} className="mr-1" data-id="element-455" />
                                Enable
                              </>}
                          </button>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </div> : <div className="bg-[#0F0F0F] rounded-lg shadow-sm border border-[#222222] p-8 text-center" data-id="element-456">
              <ServerIcon size={48} className="mx-auto text-gray-500 mb-4" data-id="element-457" />
              <h3 className="text-lg font-medium text-white mb-2" data-id="element-458">
                No MCP Servers configured
              </h3>
              <p className="text-gray-400 mb-4" data-id="element-459">
                Add your first MCP Server to manage distributed model
                deployments
              </p>
              <button onClick={openMcpDrawer} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors inline-flex items-center text-sm" data-id="element-460">
                <PlusIcon size={16} className="mr-1.5" data-id="element-461" />
                Add MCP Server
              </button>
            </div>}
          {/* MCP Server card examples */}
          <div className="mt-6" data-id="element-462">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-463">
              Example MCP Configurations
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-464">
              <div className="bg-[#0F0F0F] rounded-lg shadow-sm border border-[#222222] p-4 hover:shadow-md transition-all duration-300" data-id="element-465">
                <div className="flex items-center justify-between mb-3" data-id="element-466">
                  <div className="flex items-center" data-id="element-467">
                    <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white mr-3" data-id="element-468">
                      <CloudIcon size={20} data-id="element-469" />
                    </div>
                    <div data-id="element-470">
                      <h4 className="font-medium text-white" data-id="element-471">
                        AWS EC2 Instance
                      </h4>
                      <p className="text-xs text-gray-400" data-id="element-472">
                        High-performance cloud deployment
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-blue-900 text-blue-300 text-xs font-medium rounded-full" data-id="element-473">
                    Template
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-300 mb-3" data-id="element-474">
                  <div className="flex justify-between" data-id="element-475">
                    <span className="text-gray-400" data-id="element-476">URL:</span>
                    <span className="font-medium" data-id="element-477">
                      https://ec2-12-345-67-890.compute-1.amazonaws.com
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-478">
                    <span className="text-gray-400" data-id="element-479">Region:</span>
                    <span className="font-medium" data-id="element-480">US East (N. Virginia)</span>
                  </div>
                  <div className="flex justify-between" data-id="element-481">
                    <span className="text-gray-400" data-id="element-482">Instance Type:</span>
                    <span className="font-medium" data-id="element-483">g5.12xlarge</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-484">
                  <PlusIcon size={14} className="mr-1" data-id="element-485" />
                  Use Template
                </button>
              </div>
              <div className="bg-[#0F0F0F] rounded-lg shadow-sm border border-[#222222] p-4 hover:shadow-md transition-all duration-300" data-id="element-486">
                <div className="flex items-center justify-between mb-3" data-id="element-487">
                  <div className="flex items-center" data-id="element-488">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white mr-3" data-id="element-489">
                      <ServerIcon size={20} data-id="element-490" />
                    </div>
                    <div data-id="element-491">
                      <h4 className="font-medium text-white" data-id="element-492">
                        On-Premise Cluster
                      </h4>
                      <p className="text-xs text-gray-400" data-id="element-493">
                        Secure internal deployment
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-green-900 text-green-300 text-xs font-medium rounded-full" data-id="element-494">
                    Template
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-300 mb-3" data-id="element-495">
                  <div className="flex justify-between" data-id="element-496">
                    <span className="text-gray-400" data-id="element-497">URL:</span>
                    <span className="font-medium" data-id="element-498">
                      https://mcp-cluster.internal.company.com
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-499">
                    <span className="text-gray-400" data-id="element-500">Region:</span>
                    <span className="font-medium" data-id="element-501">Internal DC</span>
                  </div>
                  <div className="flex justify-between" data-id="element-502">
                    <span className="text-gray-400" data-id="element-503">Hardware:</span>
                    <span className="font-medium" data-id="element-504">NVIDIA A100 x8</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-505">
                  <PlusIcon size={14} className="mr-1" data-id="element-506" />
                  Use Template
                </button>
              </div>
              <div className="bg-[#0F0F0F] rounded-lg shadow-sm border border-[#222222] p-4 hover:shadow-md transition-all duration-300" data-id="element-507">
                <div className="flex items-center justify-between mb-3" data-id="element-508">
                  <div className="flex items-center" data-id="element-509">
                    <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center text-white mr-3" data-id="element-510">
                      <GlobeIcon size={20} data-id="element-511" />
                    </div>
                    <div data-id="element-512">
                      <h4 className="font-medium text-white" data-id="element-513">GCP Kubernetes</h4>
                      <p className="text-xs text-gray-400" data-id="element-514">
                        Scalable container deployment
                      </p>
                    </div>
                  </div>
                  <span className="px-2 py-1 bg-purple-900 text-purple-300 text-xs font-medium rounded-full" data-id="element-515">
                    Template
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-300 mb-3" data-id="element-516">
                  <div className="flex justify-between" data-id="element-517">
                    <span className="text-gray-400" data-id="element-518">URL:</span>
                    <span className="font-medium" data-id="element-519">
                      https://mcp-gke.company-prod.cloud.goog
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-520">
                    <span className="text-gray-400" data-id="element-521">Region:</span>
                    <span className="font-medium" data-id="element-522">us-central1</span>
                  </div>
                  <div className="flex justify-between" data-id="element-523">
                    <span className="text-gray-400" data-id="element-524">Cluster:</span>
                    <span className="font-medium" data-id="element-525">mcp-production</span>
                  </div>
                </div>
                <button className="w-full py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" data-id="element-526">
                  <PlusIcon size={14} className="mr-1" data-id="element-527" />
                  Use Template
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Apps grid */}
      {activeCategory !== 'mcp' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-528">
          {filteredApps.map(app => <div key={app.id} className="bg-[#0F0F0F] rounded-lg shadow-sm overflow-hidden border border-[#222222] transition-all duration-300 hover:scale-[1.02] hover:shadow-md hover:border-blue-800 cursor-pointer relative" onClick={() => openAppDetails(app)} data-id="element-529">
              <div className="p-4" data-id="element-530">
                <div className="flex items-center justify-between mb-2" data-id="element-531">
                  <div className="flex items-center" data-id="element-532">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${app.iconBg} text-white mr-2`} data-id="element-533">
                      {getCategoryIcon(app.category)}
                    </div>
                    <div data-id="element-534">
                      <h3 className="text-md font-semibold text-white" data-id="element-535">
                        {app.name}
                      </h3>
                      <div className="flex items-center text-xs text-gray-400" data-id="element-536">
                        <img src={app.providerLogo} alt={app.provider} className="w-3 h-3 mr-1 rounded-full" data-id="element-537" />
                        {app.provider}
                      </div>
                    </div>
                  </div>
                  {app.deployed && <span className="px-2 py-0.5 bg-green-900 text-green-300 text-xs font-medium rounded-full" data-id="element-538">
                      Deployed
                    </span>}
                </div>
                <p className="text-xs text-gray-400 mb-3 line-clamp-2 h-8" data-id="element-539">
                  {app.description}
                </p>
                <button onClick={e => {
            e.stopPropagation();
            handleAddApp(app.id);
          }} className={`w-full py-1.5 px-2 rounded-lg flex items-center justify-center transition-all duration-300 text-xs 
                    ${app.compatibility.status === 'insufficient' ? 'bg-[#1a1a1a] text-gray-500 cursor-not-allowed' : app.deployed ? app.hasUpdate ? 'bg-yellow-900 text-yellow-300 border border-yellow-800 hover:bg-yellow-800' : 'bg-blue-900 text-blue-300 border border-blue-800 hover:bg-blue-800' : addedApps.includes(app.id) ? 'bg-green-900 text-green-300 border border-green-800 hover:bg-green-800' : 'bg-blue-600 text-white hover:bg-blue-700'}`} disabled={app.compatibility.status === 'insufficient'} data-id="element-540">
                  {app.compatibility.status === 'insufficient' ? 'Insufficient Resources' : app.deployed ? app.hasUpdate ? <>
                        <ArrowUpCircleIcon size={12} className="mr-1" data-id="element-541" />
                        Update to {app.updateVersion}
                      </> : <>
                        <CheckIcon size={12} className="mr-1" data-id="element-542" />
                        Deployed
                      </> : addedApps.includes(app.id) ? <>
                      <CheckIcon size={12} className="mr-1" data-id="element-543" />
                      Added
                    </> : <>
                      <PlusIcon size={12} className="mr-1" data-id="element-544" />
                      Add
                    </>}
                </button>
              </div>
            </div>)}
        </div>}
      {/* App Details Drawer */}
      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} title={selectedApp?.name || 'App Details'} size="lg" position="right" fullHeight={true} animation="slide-in-right" data-id="element-545">
        {selectedApp && <div className="space-y-6 bg-[#0A0A0A] text-white" data-id="element-546">
            {/* Header with logo and basic info */}
            <div className="flex items-start space-x-4" data-id="element-547">
              <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border border-[#222222]" data-id="element-548">
                <img src={selectedApp.providerLogo} alt={selectedApp.provider} className="w-full h-full object-cover" data-id="element-549" />
              </div>
              <div className="flex-1" data-id="element-550">
                <div className="flex items-center justify-between" data-id="element-551">
                  <h2 className="text-xl font-bold text-white" data-id="element-552">
                    {selectedApp.name}
                  </h2>
                  <div className="flex space-x-2" data-id="element-553">
                    <span className={`px-3 py-1 ${selectedApp.color} text-sm font-medium rounded-full`} data-id="element-554">
                      {selectedApp.category === 'models' ? 'Model' : selectedApp.category === 'applications' ? 'App' : selectedApp.category === 'mcp' ? 'MCP Server' : 'Workflow'}
                    </span>
                    {selectedApp.deployed && <span className="px-3 py-1 bg-green-900 text-green-300 text-sm font-medium rounded-full" data-id="element-555">
                        Deployed
                      </span>}
                    {selectedApp.hasUpdate && <span className="px-3 py-1 bg-yellow-900 text-yellow-300 text-sm font-medium rounded-full" data-id="element-556">
                        Update Available
                      </span>}
                  </div>
                </div>
                <div className="flex items-center mt-1" data-id="element-557">
                  <p className="text-sm text-gray-400 flex items-center" data-id="element-558">
                    <img src={selectedApp.providerLogo} alt={selectedApp.provider} className="w-4 h-4 mr-1 rounded-full" data-id="element-559" />
                    Provider: {selectedApp.provider}
                  </p>
                  <span className="mx-2 text-gray-600" data-id="element-560">•</span>
                  <p className="text-sm text-gray-400" data-id="element-561">
                    Version: {selectedApp.version}
                    {selectedApp.hasUpdate && ` → ${selectedApp.updateVersion}`}
                  </p>
                </div>
              </div>
            </div>
            {deploymentStep === 0 && <>
                <div className="border-t border-b border-[#222222] py-4" data-id="element-562">
                  <h3 className="text-lg font-medium text-white mb-2" data-id="element-563">
                    Description
                  </h3>
                  <p className="text-gray-300" data-id="element-564">{selectedApp.description}</p>
                </div>
                {/* Infrastructure Compatibility Section */}
                <div className="border-b border-[#222222] pb-4" data-id="element-565">
                  <h3 className="text-lg font-medium text-white mb-3" data-id="element-566">
                    Infrastructure Compatibility
                  </h3>
                  <div className={`p-4 rounded-lg mb-4 ${getCompatibilityStatus(selectedApp.compatibility.status).bgColor}`} data-id="element-567">
                    <div className="flex items-start" data-id="element-568">
                      {getCompatibilityStatus(selectedApp.compatibility.status).icon}
                      <div className="ml-3" data-id="element-569">
                        <h4 className={`font-medium ${getCompatibilityStatus(selectedApp.compatibility.status).color}`} data-id="element-570">
                          {getCompatibilityStatus(selectedApp.compatibility.status).text}
                        </h4>
                        {selectedApp.compatibility.issues.length > 0 ? <ul className="mt-2 text-sm text-gray-700 list-disc list-inside" data-id="element-571">
                            {selectedApp.compatibility.issues.map((issue, index) => <li key={index} data-id="element-572">{issue}</li>)}
                          </ul> : <p className="mt-1 text-sm text-gray-700" data-id="element-573">
                            Your current infrastructure meets all requirements
                            for this{' '}
                            {selectedApp.category === 'models' ? 'model' : selectedApp.category === 'applications' ? 'application' : selectedApp.category === 'mcp' ? 'MCP server' : 'workflow'}
                            .
                          </p>}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-574">
                    <div data-id="element-575">
                      <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-576">
                        Required
                      </h4>
                      <div className="space-y-2" data-id="element-577">
                        <div className="flex justify-between items-center text-sm" data-id="element-578">
                          <span className="text-gray-400" data-id="element-579">GPU:</span>
                          <span className={`font-medium ${getRequirementColor(selectedApp.requirements.gpu)}`} data-id="element-580">
                            {selectedApp.requirements.gpu}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-581">
                          <span className="text-gray-400" data-id="element-582">Memory:</span>
                          <span className="font-medium text-white" data-id="element-583">
                            {selectedApp.requirements.memory}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-584">
                          <span className="text-gray-400" data-id="element-585">Storage:</span>
                          <span className="font-medium text-white" data-id="element-586">
                            {selectedApp.requirements.storage}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-587">
                      <h4 className="text-sm font-medium text-gray-300 mb-2" data-id="element-588">
                        Your System
                      </h4>
                      <div className="space-y-2" data-id="element-589">
                        <div className="flex justify-between items-center text-sm" data-id="element-590">
                          <span className="text-gray-400" data-id="element-591">GPU:</span>
                          <span className="font-medium text-white" data-id="element-592">
                            {systemInfrastructure.gpu.available} (
                            {systemInfrastructure.gpu.type})
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-593">
                          <span className="text-gray-400" data-id="element-594">Memory:</span>
                          <span className="font-medium text-white" data-id="element-595">
                            {systemInfrastructure.memory}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm" data-id="element-596">
                          <span className="text-gray-400" data-id="element-597">Storage:</span>
                          <span className="font-medium text-white" data-id="element-598">
                            {systemInfrastructure.storage}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-b border-[#222222] pt-2 pb-4" data-id="element-599">
                  <h3 className="text-lg font-medium text-white mb-3" data-id="element-600">
                    Deployment Options
                  </h3>
                  {selectedApp.deployed ? <div className="grid grid-cols-2 gap-3" data-id="element-601">
                      <button className="px-4 py-3 bg-blue-900 text-blue-300 rounded-md hover:bg-blue-800 flex items-center justify-center transition-colors" data-id="element-602">
                        <ScaleDynamicIcon size={16} className="mr-2" data-id="element-603" />
                        Scale Deployment
                      </button>
                      {selectedApp.hasUpdate ? <button className="px-4 py-3 bg-yellow-900 text-yellow-300 rounded-md hover:bg-yellow-800 flex items-center justify-center transition-colors" data-id="element-604">
                          <ArrowUpCircleIcon size={16} className="mr-2" data-id="element-605" />
                          Update to {selectedApp.updateVersion}
                        </button> : <button className="px-4 py-3 bg-amber-900 text-amber-300 rounded-md hover:bg-amber-800 flex items-center justify-center transition-colors" data-id="element-606">
                          <RefreshCwIcon size={16} className="mr-2" data-id="element-607" />
                          Reinstall
                        </button>}
                      <button className="px-4 py-3 bg-purple-900 text-purple-300 rounded-md hover:bg-purple-800 flex items-center justify-center transition-colors col-span-2" data-id="element-608">
                        <SettingsIcon size={16} className="mr-2" data-id="element-609" />
                        Configure Settings
                      </button>
                    </div> : <button onClick={() => setDeploymentStep(1)} className={`w-full px-4 py-3 rounded-md flex items-center justify-center transition-colors ${selectedApp.compatibility.status === 'insufficient' ? 'bg-gray-800 text-gray-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} disabled={selectedApp.compatibility.status === 'insufficient'} data-id="element-610">
                      <DownloadIcon size={16} className="mr-2" data-id="element-611" />
                      Deploy{' '}
                      {selectedApp.category === 'models' ? 'Model' : selectedApp.category === 'applications' ? 'App' : selectedApp.category === 'mcp' ? 'MCP Server' : 'Workflow'}
                    </button>}
                  {selectedApp.compatibility.status === 'insufficient' && <p className="mt-2 text-xs text-red-400" data-id="element-612">
                      Cannot deploy due to insufficient system resources. Please
                      upgrade your infrastructure.
                    </p>}
                </div>
              </>}
            {deploymentStep === 1 && <>
                <div className="bg-blue-900 border border-blue-800 rounded-lg p-4 mb-4" data-id="element-613">
                  <div className="flex items-start" data-id="element-614">
                    <InfoIcon size={20} className="text-blue-300 mr-3 mt-0.5" data-id="element-615" />
                    <div data-id="element-616">
                      <h3 className="font-medium text-blue-200 mb-1" data-id="element-617">
                        Deployment Configuration
                      </h3>
                      <p className="text-sm text-blue-300" data-id="element-618">
                        Please provide deployment configuration by uploading a
                        file, pasting content, or specifying a URL. This will be
                        used to set up the{' '}
                        {selectedApp.category === 'models' ? 'model' : selectedApp.category === 'applications' ? 'application' : selectedApp.category === 'mcp' ? 'MCP server' : 'workflow'}
                        .
                      </p>
                    </div>
                  </div>
                </div>
                <div className="border-b border-[#222222] pb-4 mb-4" data-id="element-619">
                  <div className="flex space-x-2 mb-4" data-id="element-620">
                    <button className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${deploymentMethod === 'upload' ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setDeploymentMethod('upload')} data-id="element-621">
                      <UploadIcon size={16} className="inline mr-1" data-id="element-622" /> Upload
                      File
                    </button>
                    <button className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${deploymentMethod === 'paste' ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setDeploymentMethod('paste')} data-id="element-623">
                      <FileTextIcon size={16} className="inline mr-1" data-id="element-624" /> Paste
                      Content
                    </button>
                    <button className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${deploymentMethod === 'url' ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setDeploymentMethod('url')} data-id="element-625">
                      <LinkIcon size={16} className="inline mr-1" data-id="element-626" /> From URL
                    </button>
                  </div>
                  {deploymentMethod === 'upload' && <div className="border-2 border-dashed border-[#333333] rounded-lg p-6 text-center" data-id="element-627">
                      <UploadIcon size={24} className="mx-auto text-gray-500 mb-2" data-id="element-628" />
                      <p className="text-sm text-gray-300 mb-2" data-id="element-629">
                        Drag and drop your configuration file here
                      </p>
                      <p className="text-xs text-gray-500 mb-3" data-id="element-630">or</p>
                      <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors" data-id="element-631">
                        Browse Files
                      </button>
                    </div>}
                  {deploymentMethod === 'paste' && <div data-id="element-632">
                      <textarea className="w-full h-32 p-3 bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="Paste your configuration content here..." data-id="element-633"></textarea>
                    </div>}
                  {deploymentMethod === 'url' && <div data-id="element-634">
                      <div className="flex items-center" data-id="element-635">
                        <input type="text" className="flex-1 p-2 bg-[#121212] border border-[#333333] rounded-l-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="https://huggingface.co/models/..." value={deploymentUrl} onChange={e => setDeploymentUrl(e.target.value)} data-id="element-636" />
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors" data-id="element-637">
                          Fetch
                        </button>
                      </div>
                      <p className="mt-2 text-xs text-gray-500" data-id="element-638">
                        Supported sources: Hugging Face, GitHub, or Amoebix
                        verified URLs
                      </p>
                    </div>}
                </div>
                <div data-id="element-639">
                  <h3 className="text-lg font-medium text-white mb-3" data-id="element-640">
                    Bootstrap Options
                  </h3>
                  <div className="space-y-2 mb-6" data-id="element-641">
                    <div className={`flex items-center p-3 border rounded-md cursor-pointer ${bootstrapOption === 'vllm' ? 'border-blue-600 bg-blue-900/30' : 'border-[#333333] hover:border-blue-600'}`} onClick={() => setBootstrapOption('vllm')} data-id="element-642">
                      <input type="radio" id="vllm" name="bootstrap" className="h-4 w-4 text-blue-600 focus:ring-blue-500 bg-[#121212] border-[#333333]" checked={bootstrapOption === 'vllm'} onChange={() => setBootstrapOption('vllm')} data-id="element-643" />
                      <label htmlFor="vllm" className="ml-3 block text-sm font-medium text-gray-300" data-id="element-644">
                        Open-source vLLM
                      </label>
                      <ChevronRightIcon size={16} className="ml-auto text-gray-500" data-id="element-645" />
                    </div>
                    <div className={`flex items-center p-3 border rounded-md cursor-pointer ${bootstrapOption === 'amoebix' ? 'border-blue-600 bg-blue-900/30' : 'border-[#333333] hover:border-blue-600'}`} onClick={() => setBootstrapOption('amoebix')} data-id="element-646">
                      <input type="radio" id="amoebix" name="bootstrap" className="h-4 w-4 text-blue-600 focus:ring-blue-500 bg-[#121212] border-[#333333]" checked={bootstrapOption === 'amoebix'} onChange={() => setBootstrapOption('amoebix')} data-id="element-647" />
                      <label htmlFor="amoebix" className="ml-3 block text-sm font-medium text-gray-300" data-id="element-648">
                        Amoebix Loader
                      </label>
                      <ChevronRightIcon size={16} className="ml-auto text-gray-500" data-id="element-649" />
                    </div>
                    <div className={`flex items-center p-3 border rounded-md cursor-pointer ${bootstrapOption === 'provider' ? 'border-blue-600 bg-blue-900/30' : 'border-[#333333] hover:border-blue-600'}`} onClick={() => setBootstrapOption('provider')} data-id="element-650">
                      <input type="radio" id="provider" name="bootstrap" className="h-4 w-4 text-blue-600 focus:ring-blue-500 bg-[#121212] border-[#333333]" checked={bootstrapOption === 'provider'} onChange={() => setBootstrapOption('provider')} data-id="element-651" />
                      <label htmlFor="provider" className="ml-3 block text-sm font-medium text-gray-300" data-id="element-652">
                        Marketplace Provider ({selectedApp.provider})
                      </label>
                      <ChevronRightIcon size={16} className="ml-auto text-gray-500" data-id="element-653" />
                    </div>
                  </div>
                  <div className="flex space-x-3" data-id="element-654">
                    <button onClick={() => setDeploymentStep(0)} className="flex-1 px-4 py-3 border border-[#333333] text-gray-300 rounded-md hover:bg-[#121212] transition-colors" data-id="element-655">
                      Back
                    </button>
                    <button onClick={() => setDeploymentStep(2)} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-656">
                      Continue
                    </button>
                  </div>
                </div>
              </>}
            {deploymentStep === 2 && <>
                <div className="bg-green-900 border border-green-800 rounded-lg p-4 mb-4" data-id="element-657">
                  <div className="flex items-start" data-id="element-658">
                    <CheckIcon size={20} className="text-green-300 mr-3 mt-0.5" data-id="element-659" />
                    <div data-id="element-660">
                      <h3 className="font-medium text-green-200 mb-1" data-id="element-661">
                        Ready to Deploy
                      </h3>
                      <p className="text-sm text-green-300" data-id="element-662">
                        Your configuration has been validated and is ready for
                        deployment. Please review the settings below before
                        proceeding.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mb-6" data-id="element-663">
                  <div className="bg-[#121212] p-4 rounded-lg border border-[#333333]" data-id="element-664">
                    <h4 className="text-sm font-medium text-gray-400 mb-2" data-id="element-665">
                      Deployment Configuration
                    </h4>
                    <div className="flex items-center justify-between" data-id="element-666">
                      <span className="text-sm text-gray-400" data-id="element-667">Source</span>
                      <span className="text-sm font-medium text-white" data-id="element-668">
                        {deploymentMethod === 'upload' ? 'Uploaded File' : deploymentMethod === 'paste' ? 'Pasted Content' : 'URL: ' + (deploymentUrl || 'Not specified')}
                      </span>
                    </div>
                  </div>
                  <div className="bg-[#121212] p-4 rounded-lg border border-[#333333]" data-id="element-669">
                    <h4 className="text-sm font-medium text-gray-400 mb-2" data-id="element-670">
                      Bootstrap Method
                    </h4>
                    <div className="flex items-center justify-between" data-id="element-671">
                      <span className="text-sm text-gray-400" data-id="element-672">
                        Selected Option
                      </span>
                      <span className="text-sm font-medium text-white" data-id="element-673">
                        {bootstrapOption === 'vllm' ? 'Open-source vLLM' : bootstrapOption === 'amoebix' ? 'Amoebix Loader' : 'Marketplace Provider'}
                      </span>
                    </div>
                  </div>
                  <div className="bg-[#121212] p-4 rounded-lg border border-[#333333]" data-id="element-674">
                    <h4 className="text-sm font-medium text-gray-400 mb-2" data-id="element-675">
                      Resource Allocation
                    </h4>
                    <div className="space-y-2" data-id="element-676">
                      <div className="flex items-center justify-between" data-id="element-677">
                        <span className="text-sm text-gray-400" data-id="element-678">GPU</span>
                        <span className={`text-sm font-medium ${getRequirementColor(selectedApp.requirements.gpu)}`} data-id="element-679">
                          {selectedApp.requirements.gpu}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-680">
                        <span className="text-sm text-gray-400" data-id="element-681">Memory</span>
                        <span className="text-sm font-medium text-white" data-id="element-682">
                          {selectedApp.requirements.memory}
                        </span>
                      </div>
                      <div className="flex items-center justify-between" data-id="element-683">
                        <span className="text-sm text-gray-400" data-id="element-684">Storage</span>
                        <span className="text-sm font-medium text-white" data-id="element-685">
                          {selectedApp.requirements.storage}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-[#121212] p-4 rounded-lg border border-[#333333]" data-id="element-686">
                    <h4 className="text-sm font-medium text-gray-400 mb-2" data-id="element-687">
                      Version Information
                    </h4>
                    <div className="flex items-center justify-between" data-id="element-688">
                      <span className="text-sm text-gray-400" data-id="element-689">Version</span>
                      <span className="text-sm font-medium text-white" data-id="element-690">
                        {selectedApp.version}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-3" data-id="element-691">
                  <button onClick={() => setDeploymentStep(1)} className="flex-1 px-4 py-3 border border-[#333333] text-gray-300 rounded-md hover:bg-[#121212] transition-colors" data-id="element-692">
                    Back
                  </button>
                  <button onClick={() => {
              setIsDrawerOpen(false);
              // In a real app, would initiate deployment here
            }} className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-693">
                    Deploy {selectedApp.name}
                  </button>
                </div>
              </>}
          </div>}
      </Drawer>
      {/* MCP Server Drawer */}
      <Drawer isOpen={isMcpDrawerOpen} onClose={() => setIsMcpDrawerOpen(false)} title={selectedMcpServer ? 'MCP Server Details' : 'Add MCP Server'} size="md" position="right" fullHeight={true} animation="slide-in-right" data-id="element-694">
        {!selectedMcpServer ? <div className="space-y-6 bg-[#0A0A0A] text-white" data-id="element-695">
            <div className="bg-blue-900 border border-blue-800 rounded-lg p-4" data-id="element-696">
              <div className="flex items-start" data-id="element-697">
                <ServerIcon size={20} className="text-blue-300 mr-3 mt-0.5" data-id="element-698" />
                <div data-id="element-699">
                  <h3 className="font-medium text-blue-200 mb-1" data-id="element-700">
                    Model Control Plane (MCP) Server
                  </h3>
                  <p className="text-sm text-blue-300" data-id="element-701">
                    Connect to an MCP server to manage model deployments across
                    multiple environments. The MCP server will handle model
                    versioning, scaling, and monitoring.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-702">
              <div data-id="element-703">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-704">
                  Server Name
                </label>
                <input type="text" className="w-full p-2 bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="Production MCP Server" value={newMcpServer.name} onChange={e => setNewMcpServer({
              ...newMcpServer,
              name: e.target.value
            })} data-id="element-705" />
              </div>
              <div data-id="element-706">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-707">
                  Server URL
                </label>
                <input type="text" className="w-full p-2 bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="https://mcp.company.com" value={newMcpServer.url} onChange={e => setNewMcpServer({
              ...newMcpServer,
              url: e.target.value
            })} data-id="element-708" />
              </div>
              <div data-id="element-709">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-710">
                  API Key
                </label>
                <div className="flex" data-id="element-711">
                  <input type={showApiKey ? 'text' : 'password'} className="flex-1 p-2 bg-[#121212] border border-[#333333] rounded-l-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="Enter your MCP API key" value={newMcpServer.apiKey} onChange={e => setNewMcpServer({
                ...newMcpServer,
                apiKey: e.target.value
              })} data-id="element-712" />
                  <button className="px-3 bg-[#1a1a1a] border-t border-r border-b border-[#333333] rounded-r-lg text-gray-400 hover:bg-[#222222]" onClick={() => setShowApiKey(!showApiKey)} data-id="element-713">
                    {showApiKey ? <EyeOffIcon size={18} data-id="element-714" /> : <EyeIcon size={18} data-id="element-715" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-716">
                  The API key will be encrypted and stored securely.
                </p>
              </div>
              <div data-id="element-717">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-718">
                  Region
                </label>
                <select className="w-full p-2 bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 text-white" value={newMcpServer.region} onChange={e => setNewMcpServer({
              ...newMcpServer,
              region: e.target.value
            })} data-id="element-719">
                  <option value="US West" data-id="element-720">US West</option>
                  <option value="US East" data-id="element-721">US East</option>
                  <option value="EU West" data-id="element-722">EU West</option>
                  <option value="EU Central" data-id="element-723">EU Central</option>
                  <option value="Asia Pacific" data-id="element-724">Asia Pacific</option>
                  <option value="Internal DC" data-id="element-725">Internal DC</option>
                </select>
              </div>
              <div data-id="element-726">
                <label className="block text-sm font-medium text-gray-300 mb-1" data-id="element-727">
                  Description (Optional)
                </label>
                <textarea className="w-full p-2 bg-[#121212] border border-[#333333] rounded-lg focus:ring-blue-500 focus:border-blue-500 h-20 text-white" placeholder="Add a description for this MCP server..." value={newMcpServer.description} onChange={e => setNewMcpServer({
              ...newMcpServer,
              description: e.target.value
            })} data-id="element-728"></textarea>
              </div>
              <div className="pt-2" data-id="element-729">
                <button onClick={testMcpConnection} className="w-full mb-3 px-4 py-3 bg-[#121212] text-gray-300 rounded-md hover:bg-[#1a1a1a] transition-colors flex items-center justify-center" disabled={mcpTestStatus === 'testing'} data-id="element-730">
                  {mcpTestStatus === 'testing' ? <>
                      <RefreshCwIcon size={16} className="mr-2 animate-spin" data-id="element-731" />
                      Testing Connection...
                    </> : <>
                      <LinkIcon size={16} className="mr-2" data-id="element-732" />
                      Test Connection
                    </>}
                </button>
                {mcpTestStatus === 'success' && <div className="mb-3 p-3 bg-green-900 border border-green-800 rounded-lg text-sm text-green-300 flex items-start" data-id="element-733">
                    <CheckCircleIcon size={16} className="text-green-400 mr-2 mt-0.5" data-id="element-734" />
                    <span data-id="element-735">
                      Connection successful! The MCP server is reachable and the
                      API key is valid.
                    </span>
                  </div>}
                {mcpTestStatus === 'failed' && <div className="mb-3 p-3 bg-red-900 border border-red-800 rounded-lg text-sm text-red-300 flex items-start" data-id="element-736">
                    <AlertCircleIcon size={16} className="text-red-400 mr-2 mt-0.5" data-id="element-737" />
                    <span data-id="element-738">
                      Connection failed. Please check the server URL and API
                      key.
                    </span>
                  </div>}
                <button onClick={handleAddMcpServer} className="w-full px-4 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center" disabled={!newMcpServer.name || !newMcpServer.url || !newMcpServer.apiKey} data-id="element-739">
                  <SaveIcon size={16} className="mr-2" data-id="element-740" />
                  Add MCP Server
                </button>
              </div>
            </div>
          </div> : <div className="space-y-6 bg-[#0A0A0A] text-white" data-id="element-741">
            <div className="flex items-center justify-between bg-[#121212] p-4 rounded-lg border border-[#222222]" data-id="element-742">
              <div className="flex items-center" data-id="element-743">
                <div className="h-12 w-12 bg-slate-600 rounded-full flex items-center justify-center text-white mr-3" data-id="element-744">
                  <ServerIcon size={24} data-id="element-745" />
                </div>
                <div data-id="element-746">
                  <h3 className="text-lg font-medium text-white" data-id="element-747">
                    {selectedMcpServer.name}
                  </h3>
                  <p className="text-sm text-gray-400" data-id="element-748">
                    {selectedMcpServer.url}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-end" data-id="element-749">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${selectedMcpServer.status === 'active' ? 'bg-green-900 text-green-300' : 'bg-gray-800 text-gray-300'}`} data-id="element-750">
                  {selectedMcpServer.status === 'active' ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-gray-400 mt-1" data-id="element-751">
                  {selectedMcpServer.region}
                </span>
              </div>
            </div>
            <div className="border-t border-b border-[#222222] py-4" data-id="element-752">
              <h3 className="text-base font-medium text-white mb-2" data-id="element-753">
                Description
              </h3>
              <p className="text-gray-300" data-id="element-754">
                {selectedMcpServer.description || 'No description provided.'}
              </p>
            </div>
            <div data-id="element-755">
              <h3 className="text-base font-medium text-white mb-3" data-id="element-756">
                Connection Details
              </h3>
              <div className="space-y-3 bg-[#121212] p-4 rounded-lg" data-id="element-757">
                <div className="flex justify-between" data-id="element-758">
                  <span className="text-sm text-gray-400" data-id="element-759">URL:</span>
                  <span className="text-sm font-medium text-white" data-id="element-760">
                    {selectedMcpServer.url}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-761">
                  <span className="text-sm text-gray-400" data-id="element-762">API Key:</span>
                  <span className="text-sm font-medium text-white" data-id="element-763">
                    {selectedMcpServer.apiKey}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-764">
                  <span className="text-sm text-gray-400" data-id="element-765">Last Sync:</span>
                  <span className="text-sm font-medium text-white" data-id="element-766">
                    {selectedMcpServer.lastSync}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-767">
                  <span className="text-sm text-gray-400" data-id="element-768">
                    Models Available:
                  </span>
                  <span className="text-sm font-medium text-white" data-id="element-769">
                    {selectedMcpServer.models}
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2" data-id="element-770">
              <button onClick={() => toggleMcpServerStatus(selectedMcpServer.id)} className={`py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center ${selectedMcpServer.status === 'active' ? 'bg-red-900 text-red-300 hover:bg-red-800' : 'bg-green-900 text-green-300 hover:bg-green-800'}`} data-id="element-771">
                {selectedMcpServer.status === 'active' ? <>
                    <ToggleRightIcon size={16} className="mr-1.5" data-id="element-772" />
                    Disable Server
                  </> : <>
                    <ToggleLeftIcon size={16} className="mr-1.5" data-id="element-773" />
                    Enable Server
                  </>}
              </button>
              <button className="py-2 px-4 bg-blue-900 text-blue-300 rounded-md hover:bg-blue-800 text-sm font-medium flex items-center justify-center" data-id="element-774">
                <RefreshCwIcon size={16} className="mr-1.5" data-id="element-775" />
                Sync Models
              </button>
              <button className="py-2 px-4 bg-purple-900 text-purple-300 rounded-md hover:bg-purple-800 text-sm font-medium flex items-center justify-center col-span-2" data-id="element-776">
                <SettingsIcon size={16} className="mr-1.5" data-id="element-777" />
                Edit Configuration
              </button>
            </div>
            <div className="border-t border-[#222222] pt-4" data-id="element-778">
              <h3 className="text-base font-medium text-white mb-3" data-id="element-779">
                Advanced Settings
              </h3>
              <div className="space-y-3" data-id="element-780">
                <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg" data-id="element-781">
                  <div className="flex items-center" data-id="element-782">
                    <ShieldIcon size={16} className="text-gray-400 mr-2" data-id="element-783" />
                    <span className="text-sm text-gray-300" data-id="element-784">
                      Secure Connection (TLS)
                    </span>
                  </div>
                  <span className="text-sm font-medium text-green-400" data-id="element-785">
                    Enabled
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg" data-id="element-786">
                  <div className="flex items-center" data-id="element-787">
                    <CpuIcon size={16} className="text-gray-400 mr-2" data-id="element-788" />
                    <span className="text-sm text-gray-300" data-id="element-789">
                      Resource Sharing
                    </span>
                  </div>
                  <span className="text-sm font-medium text-green-400" data-id="element-790">
                    Enabled
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#121212] rounded-lg" data-id="element-791">
                  <div className="flex items-center" data-id="element-792">
                    <UserIcon size={16} className="text-gray-400 mr-2" data-id="element-793" />
                    <span className="text-sm text-gray-300" data-id="element-794">
                      Authentication Mode
                    </span>
                  </div>
                  <span className="text-sm font-medium text-white" data-id="element-795">
                    API Key
                  </span>
                </div>
              </div>
            </div>
          </div>}
      </Drawer>
      {/* Offline Installation Drawer */}
      <Drawer isOpen={isOfflineDrawerOpen} onClose={() => setIsOfflineDrawerOpen(false)} title="Offline Installation" size="md" fullHeight={true} animation="slide-in-right" data-id="element-796">
        <div className="space-y-6 bg-[#0A0A0A] text-white" data-id="element-797">
          <div className="bg-blue-900 border border-blue-800 rounded-lg p-4" data-id="element-798">
            <div className="flex items-start" data-id="element-799">
              <PackageIcon size={20} className="text-blue-300 mr-3 mt-0.5" data-id="element-800" />
              <div data-id="element-801">
                <h3 className="font-medium text-blue-200 mb-1" data-id="element-802">
                  Offline Installation
                </h3>
                <p className="text-sm text-blue-300" data-id="element-803">
                  Install or update marketplace items from offline packages.
                  Only Amoebix verified packages are supported for security
                  reasons.
                </p>
              </div>
            </div>
          </div>
          <div className="border-b border-[#222222] pb-6" data-id="element-804">
            <h3 className="text-lg font-medium text-white mb-4" data-id="element-805">
              Installation Method
            </h3>
            <div className="flex space-x-2 mb-4" data-id="element-806">
              <button className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${offlineInstallMethod === 'upload' ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setOfflineInstallMethod('upload')} data-id="element-807">
                <UploadIcon size={16} className="inline mr-1" data-id="element-808" /> Upload Package
              </button>
              <button className={`flex-1 py-2 px-3 rounded-md text-sm font-medium ${offlineInstallMethod === 'url' ? 'bg-blue-600 text-white' : 'bg-[#121212] text-gray-300 hover:bg-[#1a1a1a]'}`} onClick={() => setOfflineInstallMethod('url')} data-id="element-809">
                <LinkIcon size={16} className="inline mr-1" data-id="element-810" /> From URL
              </button>
            </div>
            {offlineInstallMethod === 'upload' && <div className="border-2 border-dashed border-[#333333] rounded-lg p-6 text-center" data-id="element-811">
                <UploadIcon size={24} className="mx-auto text-gray-500 mb-2" data-id="element-812" />
                <p className="text-sm text-gray-300 mb-2" data-id="element-813">
                  Drag and drop your package file here (.amx)
                </p>
                <p className="text-xs text-gray-500 mb-3" data-id="element-814">or</p>
                <button className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors" data-id="element-815">
                  Browse Files
                </button>
              </div>}
            {offlineInstallMethod === 'url' && <div data-id="element-816">
                <div className="flex items-center" data-id="element-817">
                  <input type="text" className="flex-1 p-2 bg-[#121212] border border-[#333333] rounded-l-lg focus:ring-blue-500 focus:border-blue-500 text-white" placeholder="https://amoebix.ai/packages/..." value={offlineInstallUrl} onChange={e => setOfflineInstallUrl(e.target.value)} data-id="element-818" />
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors" onClick={handleVerifyOfflinePackage} data-id="element-819">
                    Verify
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500" data-id="element-820">
                  Only URLs from amoebix.ai domain or verified partners are
                  accepted
                </p>
              </div>}
          </div>
          {/* Verification Status */}
          {verificationStatus !== 'pending' && <div className={`p-4 rounded-lg ${verificationStatus === 'verified' ? 'bg-green-900 border border-green-800' : verificationStatus === 'verifying' ? 'bg-blue-900 border border-blue-800' : 'bg-red-900 border border-red-800'}`} data-id="element-821">
              <div className="flex items-start" data-id="element-822">
                {verificationStatus === 'verified' && <BadgeCheckIcon size={20} className="text-green-300 mr-3 mt-0.5" data-id="element-823" />}
                {verificationStatus === 'verifying' && <RefreshCwIcon size={20} className="text-blue-300 mr-3 mt-0.5 animate-spin" data-id="element-824" />}
                {verificationStatus === 'failed' && <AlertCircleIcon size={20} className="text-red-300 mr-3 mt-0.5" data-id="element-825" />}
                <div data-id="element-826">
                  <h3 className={`font-medium mb-1 ${verificationStatus === 'verified' ? 'text-green-200' : verificationStatus === 'verifying' ? 'text-blue-200' : 'text-red-200'}`} data-id="element-827">
                    {verificationStatus === 'verified' ? 'Package Verified' : verificationStatus === 'verifying' ? 'Verifying Package...' : 'Verification Failed'}
                  </h3>
                  <p className={`text-sm ${verificationStatus === 'verified' ? 'text-green-300' : verificationStatus === 'verifying' ? 'text-blue-300' : 'text-red-300'}`} data-id="element-828">
                    {verificationStatus === 'verified' ? 'This package is verified by Amoebix and safe to install.' : verificationStatus === 'verifying' ? 'Please wait while we verify the package signature...' : 'This package could not be verified. Only install packages from trusted sources.'}
                  </p>
                </div>
              </div>
            </div>}
          <div className="pt-2" data-id="element-829">
            <button className={`w-full px-4 py-3 rounded-md flex items-center justify-center transition-colors ${verificationStatus === 'verified' ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-[#1a1a1a] text-gray-500 cursor-not-allowed'}`} disabled={verificationStatus !== 'verified'} data-id="element-830">
              <DownloadIcon size={16} className="mr-2" data-id="element-831" />
              Install Package
            </button>
            {verificationStatus === 'failed' && <p className="mt-2 text-xs text-center text-red-400" data-id="element-832">
                Cannot install unverified packages. Please try another source.
              </p>}
          </div>
          <div className="border-t border-[#222222] pt-4 mt-4" data-id="element-833">
            <div className="flex items-center" data-id="element-834">
              <InfoIcon size={16} className="text-gray-500 mr-2" data-id="element-835" />
              <p className="text-xs text-gray-400" data-id="element-836">
                For security reasons, only Amoebix verified packages can be
                installed. This helps protect your system from malicious
                software.
              </p>
            </div>
          </div>
        </div>
      </Drawer>
      {/* Help Drawer */}
      <Drawer isOpen={isHelpDrawerOpen} onClose={() => setIsHelpDrawerOpen(false)} title="Marketplace Help" size="md" fullHeight={true} animation="slide-in-right" data-id="element-837">
        <div className="space-y-6 bg-[#0A0A0A] text-white" data-id="element-838">
          <div className="bg-blue-900 border border-blue-800 rounded-lg p-4" data-id="element-839">
            <div className="flex items-start" data-id="element-840">
              <InfoIcon size={20} className="text-blue-300 mr-3 mt-0.5" data-id="element-841" />
              <div data-id="element-842">
                <h3 className="font-medium text-blue-200 mb-1" data-id="element-843">
                  Getting Started
                </h3>
                <p className="text-sm text-blue-300" data-id="element-844">
                  The Ghisha Marketplace provides a curated collection of AI
                  models, applications, and workflow templates that can be
                  deployed to your Ghisha instance.
                </p>
              </div>
            </div>
          </div>
          <div data-id="element-845">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-846">
              Marketplace Categories
            </h3>
            <div className="space-y-3" data-id="element-847">
              <div className="flex items-start" data-id="element-848">
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white mr-3" data-id="element-849">
                  <DatabaseIcon size={16} data-id="element-850" />
                </div>
                <div data-id="element-851">
                  <h4 className="font-medium text-white" data-id="element-852">Models</h4>
                  <p className="text-sm text-gray-300" data-id="element-853">
                    Large language models from providers like Meta, Google,
                    DeepSeek, and more.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-854">
                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white mr-3" data-id="element-855">
                  <BotIcon size={16} data-id="element-856" />
                </div>
                <div data-id="element-857">
                  <h4 className="font-medium text-white" data-id="element-858">Applications</h4>
                  <p className="text-sm text-gray-300" data-id="element-859">
                    Pre-built AI applications for specific use cases like
                    customer support, legal assistance, and more.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-860">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-white mr-3" data-id="element-861">
                  <GitBranchIcon size={16} data-id="element-862" />
                </div>
                <div data-id="element-863">
                  <h4 className="font-medium text-white" data-id="element-864">Workflows</h4>
                  <p className="text-sm text-gray-300" data-id="element-865">
                    Automated AI workflow templates for complex processes and
                    multi-step operations.
                  </p>
                </div>
              </div>
              <div className="flex items-start" data-id="element-866">
                <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white mr-3" data-id="element-867">
                  <ServerIcon size={16} data-id="element-868" />
                </div>
                <div data-id="element-869">
                  <h4 className="font-medium text-white" data-id="element-870">MCP Servers</h4>
                  <p className="text-sm text-gray-300" data-id="element-871">
                    Model Control Plane servers for managing distributed model
                    deployments and configurations across environments.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#222222] pt-4" data-id="element-872">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-873">
              Deployment Options
            </h3>
            <div className="space-y-4" data-id="element-874">
              <div data-id="element-875">
                <h4 className="font-medium text-white mb-1" data-id="element-876">
                  Upload Configuration
                </h4>
                <p className="text-sm text-gray-300" data-id="element-877">
                  Upload a JSON or YAML configuration file with deployment
                  parameters.
                </p>
              </div>
              <div data-id="element-878">
                <h4 className="font-medium text-white mb-1" data-id="element-879">
                  Paste Configuration
                </h4>
                <p className="text-sm text-gray-300" data-id="element-880">
                  Paste configuration content directly into the deployment form.
                </p>
              </div>
              <div data-id="element-881">
                <h4 className="font-medium text-white mb-1" data-id="element-882">
                  URL Configuration
                </h4>
                <p className="text-sm text-gray-300" data-id="element-883">
                  Provide a URL to a Hugging Face model, GitHub repository, or
                  Amoebix verified URL.
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-[#222222] pt-4" data-id="element-884">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-885">
              Offline Installation
            </h3>
            <p className="text-sm text-gray-300 mb-3" data-id="element-886">
              You can install packages offline using the Offline Installation
              option. This is useful for:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-300 mb-3 space-y-1" data-id="element-887">
              <li data-id="element-888">Installing custom models not available in the marketplace</li>
              <li data-id="element-889">Updating existing applications with new versions</li>
              <li data-id="element-890">Installing packages in air-gapped environments</li>
            </ul>
            <p className="text-sm text-gray-300 mb-3" data-id="element-891">
              For security reasons, only Amoebix verified packages can be
              installed.
            </p>
          </div>
          <div className="border-t border-[#222222] pt-4" data-id="element-892">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-893">MCP Servers</h3>
            <p className="text-sm text-gray-300 mb-3" data-id="element-894">
              Model Control Plane (MCP) servers allow you to manage model
              deployments across multiple environments. Benefits include:
            </p>
            <ul className="list-disc list-inside text-sm text-gray-300 mb-3 space-y-1" data-id="element-895">
              <li data-id="element-896">Centralized management of models across environments</li>
              <li data-id="element-897">Automated scaling and load balancing</li>
              <li data-id="element-898">Version control and rollback capabilities</li>
              <li data-id="element-899">Distributed inference for high availability</li>
            </ul>
            <p className="text-sm text-gray-300 mb-3" data-id="element-900">
              Configure MCP servers with your existing infrastructure or deploy
              new ones through the marketplace.
            </p>
          </div>
          <div className="border-t border-[#222222] pt-4" data-id="element-901">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-902">
              Verified Sources
            </h3>
            <p className="text-sm text-gray-300 mb-3" data-id="element-903">
              MemBrain maintains a list of verified sources for model
              deployment. You can add your own trusted sources to this
              whitelist.
            </p>
            <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm" data-id="element-904">
              Manage Whitelist
            </button>
          </div>
          <div className="border-t border-[#222222] pt-4" data-id="element-905">
            <h3 className="text-lg font-medium text-white mb-3" data-id="element-906">
              Need More Help?
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-907">
              <button className="p-3 bg-[#121212] hover:bg-[#1a1a1a] rounded-lg text-white transition-colors flex flex-col items-center" data-id="element-908">
                <BookOpenIcon size={24} className="mb-2 text-gray-300" data-id="element-909" />
                <span className="text-sm font-medium" data-id="element-910">Documentation</span>
              </button>
              <button className="p-3 bg-[#121212] hover:bg-[#1a1a1a] rounded-lg text-white transition-colors flex flex-col items-center" data-id="element-911">
                <MessageSquareIcon size={24} className="mb-2 text-gray-300" data-id="element-912" />
                <span className="text-sm font-medium" data-id="element-913">Contact Support</span>
              </button>
            </div>
          </div>
        </div>
      </Drawer>
      <style jsx global data-id="element-914">{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out forwards;
        }
      `}</style>
    </div>;
}